import { Wrapper } from '@googlemaps/react-wrapper'
import { ZonesMapContainer } from 'pages/Zones/Zones.styled'
import { useEffect, useRef, useState } from 'react'
import { ILocationState } from 'types/locations.interface'
import isValidCoordinates from 'is-valid-coordinates'
import { tokens } from 'styling/tokens'


export const LocationMapTab = ({ setState, state }: ILocationMapTab) => {
    const [map, setMap] = useState<google.maps.Map | null>(null)

    useEffect(() => {
        if (map && state.coordinates.latitude && state.coordinates.longitude && isValidCoordinates.latitude(parseFloat(state.coordinates.latitude.toString())) && isValidCoordinates.longitude(parseFloat(state.coordinates.longitude.toString()))) {
            map.setCenter({ lng: Number(state.coordinates.longitude), lat: Number(state.coordinates.latitude) })
            map.setZoom(10)
        }
    }, [map])

    return <div className='App-map'>
        <Wrapper apiKey={process.env.REACT_APP_GOOGLE_KEY} version='beta' libraries={['marker']}>
            <Map setMap={setMap} state={state} setState={setState} />
        </Wrapper>
    </div>
}


const Map = ({ setMap, state, setState }) => {
    const ref = useRef<HTMLDivElement | null>(null)
    const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null)
    const polygonRef = useRef<google.maps.Polygon | null>(null)
    const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null)
    const historyRef = useRef<{ lat: number; lng: number }[][]>([])
    const applyingSnapshotRef = useRef<boolean>(false)
    const undoControlRef = useRef<HTMLDivElement | null>(null)
    const mapOptions = {
        gestureHandling: 'greedy',
        zoom: 5,
        mapId: '4504f8b37365c3d0',
        center: {
            lat: 0,
            lng: 0,
        },
        disableDefaultUI: true,
        mapTypeId: 'satellite' as google.maps.MapTypeId,
        styles: [
            {
                featureType: 'all',
                elementType: 'labels',
                stylers: [
                    { visibility: 'off' }
                ]
            }
        ]
    }




    useEffect(() => {
        const gMap = new google.maps.Map(ref.current as any, mapOptions)
        setMapInstance(gMap)
        if (setMap) setMap(gMap)
        // Seed history with an empty baseline so the first Undo removes polygon
        historyRef.current = [[]]
    }, [])

    // Initialize DrawingManager and controls when map is ready
    useEffect(() => {
        if (!mapInstance) return

        // If there is existing path in state, render it
        if (state?.path && state.path.length > 2 && !polygonRef.current) {
            const path = state.path.map(p => ({ lat: p.lat, lng: p.lng }))
            const poly = new google.maps.Polygon({
                paths: path,
                map: mapInstance,
                editable: true,
                draggable: false,
                clickable: true,
            })
            polygonRef.current = poly

            // Fit bounds to polygon
            const bounds = new google.maps.LatLngBounds()
            path.forEach(pt => bounds.extend(pt))
            if (!bounds.isEmpty()) {
                mapInstance.fitBounds(bounds)
            }

            // Seed history with initial state
            pushHistorySnapshot(poly)
            attachPathListeners(poly)
        }

        if (!drawingManagerRef.current) {
            void (async () => {
                // Dynamically load the drawing library to avoid changing Wrapper libraries
                const drawingLib = await (google.maps as any).importLibrary('drawing')

                const dm = new drawingLib.DrawingManager({
                    drawingMode: drawingLib.OverlayType.POLYGON,
                    drawingControl: true,
                    drawingControlOptions: {
                        position: google.maps.ControlPosition.TOP_CENTER,
                        drawingModes: [drawingLib.OverlayType.POLYGON],
                    },
                    polygonOptions: {
                        editable: true,
                        draggable: false,
                        clickable: true,
                    },
                })
                dm.setMap(mapInstance)
                drawingManagerRef.current = dm

                google.maps.event.addListener(dm, 'overlaycomplete', (e: any) => {
                    if (e.type !== drawingLib.OverlayType.POLYGON) return

                    // Remove any existing polygon to keep a single one
                    if (polygonRef.current) {
                        polygonRef.current.setMap(null)
                    }

                    const poly: google.maps.Polygon = e.overlay
                    polygonRef.current = poly
                    // Stop drawing more shapes after one polygon is created
                    dm.setDrawingMode(null)

                    // Ensure options
                    poly.setEditable(true)
                    poly.setDraggable(false)

                    updateStateFromPolygon(poly)
                    // Seed history with initial draw after baseline
                    pushHistorySnapshot(poly)
                    attachPathListeners(poly)
                })
            })()
        }

        // Add on-screen Undo control
        if (!undoControlRef.current) {
            const controlDiv = document.createElement('div')
            controlDiv.style.background = tokens.surface.raised
            controlDiv.style.border = `1px solid ${tokens.border.hairline}`
            controlDiv.style.borderRadius = tokens.radius.control
            controlDiv.style.boxShadow = tokens.shadow.card
            controlDiv.style.color = tokens.text.primary
            controlDiv.style.margin = '0.625rem'
            controlDiv.style.padding = '0.5rem 0.75rem'
            controlDiv.style.cursor = 'pointer'
            controlDiv.style.font = '500 0.875rem system-ui, -apple-system, Segoe UI, Roboto, Arial'
            controlDiv.style.userSelect = 'none'
            controlDiv.setAttribute('title', 'Undo last polygon change')
            controlDiv.setAttribute('role', 'button')
            controlDiv.textContent = 'Undo'

            google.maps.event.addDomListener(controlDiv, 'click', () => {
                // If drawing in progress (no polygon yet), cancel current sketch and restart draw mode
                const dm: any = drawingManagerRef.current as any
                const hasPolygon = !!polygonRef.current
                const isDrawing = dm && typeof dm.getDrawingMode === 'function' && dm.getDrawingMode() === (google.maps as any).drawing.OverlayType.POLYGON && !hasPolygon
                if (isDrawing) {
                    dm.setDrawingMode(null)
                    // restart polygon drawing to let user start fresh
                    setTimeout(() => dm.setDrawingMode((google.maps as any).drawing.OverlayType.POLYGON), 0)
                    return
                }

                const history = historyRef.current
                if (history.length > 1) {
                    history.pop()
                    const prev = history[history.length - 1]
                    applySnapshot(prev)
                }
            })

            mapInstance.controls[google.maps.ControlPosition.TOP_LEFT].push(controlDiv)
            undoControlRef.current = controlDiv
        }

        // Cleanup on unmount
        return () => {
            if (drawingManagerRef.current) {
                drawingManagerRef.current.setMap(null)
                drawingManagerRef.current = null
            }
            if (undoControlRef.current) {
                // Remove control from map controls
                const controls = mapInstance.controls[google.maps.ControlPosition.TOP_LEFT]
                for (let i = controls.getLength() - 1; i >= 0; i--) {
                    if (controls.getAt(i) === undoControlRef.current) {
                        controls.removeAt(i)
                        break
                    }
                }
                undoControlRef.current = null
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapInstance])

    const updateStateFromPolygon = (poly: google.maps.Polygon) => {
        const path = poly.getPath()
        const coords = [] as { lat: number; lng: number }[]
        for (let i = 0; i < path.getLength(); i++) {
            const ll = path.getAt(i)
            coords.push({ lat: ll.lat(), lng: ll.lng() })
        }
        setState(prev => ({ ...prev, path: coords }))
    }

    const attachPathListeners = (poly: google.maps.Polygon) => {
        const path = poly.getPath()
        const onChange = () => {
            if (applyingSnapshotRef.current) return
            updateStateFromPolygon(poly)
            pushHistorySnapshot(poly)
        }
        path.addListener('insert_at', onChange)
        path.addListener('set_at', onChange)
        path.addListener('remove_at', onChange)
    }

    const pushHistorySnapshot = (poly: google.maps.Polygon) => {
        const path = poly.getPath()
        const snapshot: { lat: number; lng: number }[] = []
        for (let i = 0; i < path.getLength(); i++) {
            const ll = path.getAt(i)
            snapshot.push({ lat: ll.lat(), lng: ll.lng() })
        }
        // Avoid pushing duplicate consecutive snapshots
        const history = historyRef.current
        const last = history[history.length - 1]
        const isSame = last && last.length === snapshot.length && last.every((p, idx) => p.lat === snapshot[idx].lat && p.lng === snapshot[idx].lng)
        if (!isSame) {
            history.push(snapshot)
        }
    }

    const applySnapshot = (coords: { lat: number; lng: number }[]) => {
        // If snapshot is empty, remove polygon and clear state
        if (coords.length === 0) {
            if (polygonRef.current) {
                polygonRef.current.setMap(null)
                polygonRef.current = null
            }
            setState(prev => ({ ...prev, path: [] }))
            // Allow drawing a new polygon immediately
            if (drawingManagerRef.current && typeof (drawingManagerRef.current as any).setDrawingMode === 'function') {
                ; (drawingManagerRef.current as any).setDrawingMode((google.maps as any).drawing.OverlayType.POLYGON)
            }
            return
        }

        if (!polygonRef.current) return
        applyingSnapshotRef.current = true
        const newPath = coords.map(c => ({ lat: c.lat, lng: c.lng }))
        polygonRef.current.setPath(newPath as any)
        updateStateFromPolygon(polygonRef.current)
        applyingSnapshotRef.current = false
    }

    // Keyboard shortcuts removed; using on-screen control instead

    return <ZonesMapContainer ref={ref} id='map' />
}



interface ILocationMapTab {
    state: ILocationState
    setState: React.Dispatch<React.SetStateAction<ILocationState>>
    handleSubmit: () => void
}